/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 *
 * PACK BUILDER 2026 — split-view configurator
 * Long-scroll steps + sticky visual anchor + unified summary. Flat prebuilt
 * packs only: every path resolves to one real pack product / one cart line.
 */

class PackBuilder {
  constructor(container) {
    this.container = container || document.querySelector('[id^="PackBuilder"]');
    if (!this.container) return;
    this.events = new AbortController();
    this.timers = new Set();

    this.configs = this.readJson("pb-bundles").filter((config) => config.enabled !== false && config.variant_id);
    this.settings = this.readJson("pb-config");
    this.translations = this.settings.translations || {};
    this.steps = ["board_type", "board_family", "board_model", "rig_model", "sail_size"];
    this.labels = {
      board_type: "Board Type",
      board_family: "Board Family",
      board_model: "Board Model / Size",
      rig_model: "Rig",
      sail_size: "Sail Size",
    };
    this.reducedMotion =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.currentVisual = null;

    // Steps the engine may auto-select when only one option is available, so the
    // shopper never faces a dead 1-card screen. Kept deliberately narrow: the
    // emotionally meaningful choices (size, rig, sail) are always made by hand.
    this.autoSkippable = new Set(["board_family"]);
    this.autoResolved = new Set();

    this.state = {
      currentStep: 1,
      selections: {},
      resolvedConfig: null,
    };

    this.cacheElements();
    this.bindEvents();
    this.exposePublicApi();

    if (!this.configs.length) {
      this.showEmptyState();
      return;
    }

    // Fail safe rather than silently resolve the wrong variant: if two configs
    // share the same step-key path, the cart could add the wrong pack.
    if (!this.assertUniqueResolution()) {
      this.showEmptyState();
      return;
    }

    this.autoResolveSingles();
    this.renderAllSteps();
    this.updateNav();
    this.updateSummary();
    this.updateVisual();
  }

  listen(target, type, handler) {
    target?.addEventListener(type, handler, { signal: this.events.signal });
  }

  delay(handler, ms) {
    const timer = setTimeout(() => { this.timers.delete(timer); handler(); }, ms);
    this.timers.add(timer);
  }

  destroy() {
    this.destroyed = true;
    this.events?.abort();
    this.timers?.forEach(clearTimeout);
    if (window.packBuilderInstance === this) delete window.EXOCET_PACK_BUILDER;
  }

  readJson(id) {
    try {
      return JSON.parse(document.getElementById(id)?.textContent || (id === "pb-bundles" ? "[]" : "{}"));
    } catch (error) {
      console.error(`Pack Builder could not parse ${id}`, error);
      return id === "pb-bundles" ? [] : {};
    }
  }

  cacheElements() {
    this.configurator = this.container.querySelector("#pb-configurator");
    this.optionGroups = this.container.querySelectorAll("[data-option-group]");
    this.stepEls = this.container.querySelectorAll(".pb-step[data-step]");
    this.navItems = this.container.querySelectorAll("[data-stepnav]");

    this.visual = {
      media: this.container.querySelector("#pb-visual-media"),
      img: this.container.querySelector("#pb-visual-img"),
      overlay: this.container.querySelector("#pb-visual-overlay"),
    };

    this.summaryNodes = this.container.querySelectorAll("[data-summary]");
    this.summaryImages = this.container.querySelectorAll("[data-summary-img]");
    // BIS lives in two places (desktop summary + review panel) so it shows on
    // every viewport — the summary is hidden on mobile.
    this.bisBlocks = Array.from(this.container.querySelectorAll(".pb-bis"));
    this.ctas = Array.from(this.container.querySelectorAll('[data-action="add-pack"]'));
    this.mobilebar = this.container.querySelector("#pb-mobilebar");

    this.review = {
      section: this.container.querySelector("#pb-review"),
      title: this.container.querySelector("#pb-review-title"),
      badge: this.container.querySelector("#pb-review-badge"),
      subtitle: this.container.querySelector("#pb-review-subtitle"),
      items: this.container.querySelector("#pb-review-items"),
      viewDetails: this.container.querySelector("#pb-view-details"),
      chooseSize: this.container.querySelector("#pb-choose-size"),
    };
    this.reviewSubtitleDefault = this.review.subtitle ? this.review.subtitle.textContent : "";
  }

  bindEvents() {
    this.listen(this.container, "click", (event) => {
      const option = event.target.closest("[data-pack-builder-option]");
      if (option) {
        this.handleOptionSelect(option);
        return;
      }

      const nav = event.target.closest("[data-stepnav]");
      if (nav) {
        this.handleNavClick(nav);
        return;
      }

      const action = event.target.closest("[data-action]");
      if (!action) return;

      const type = action.dataset.action;
      if (type === "restart") {
        event.preventDefault();
        this.restart();
      } else if (type === "choose-sail-size") {
        event.preventDefault();
        this.backToSailSizeStep();
      } else if (type === "add-pack") {
        event.preventDefault();
        this.handleAddPackToCart(action);
      }
    });
  }

  exposePublicApi() {
    window.EXOCET_PACK_BUILDER = {
      getState: () => ({
        currentStep: this.state.currentStep,
        selections: { ...this.state.selections },
        resolvedConfig: this.state.resolvedConfig,
      }),
      getStepStatus: () =>
        this.steps.map((key) => ({
          key,
          label: this.labels[key],
          complete: Boolean(this.state.selections[key]),
          auto: this.autoResolved.has(key),
          value: this.state.selections[key] || null,
        })),
      getResolvedConfig: () => this.state.resolvedConfig,
      restart: () => this.restart(),
    };
  }

  /* ---------------------------------------------------------------
     Selection + navigation
  --------------------------------------------------------------- */

  handleOptionSelect(option) {
    if (option.disabled || option.getAttribute("aria-disabled") === "true") return;

    const key = option.dataset.key;
    const value = option.dataset.value;
    const stepIndex = this.steps.indexOf(key);
    if (stepIndex === -1) return;

    // Re-selecting the same value is a no-op: don't wipe valid downstream choices.
    if (this.state.selections[key] === value) return;

    // Snapshot downstream USER choices (auto-selected ones are recomputed), then
    // clear this step on. After applying the new value we re-apply any preserved
    // choice that is still offered + in stock, so editing an earlier step keeps a
    // compatible later selection instead of forcing a full re-pick.
    const downstream = this.steps.slice(stepIndex + 1);
    const preserved = {};
    downstream.forEach((stepKey) => {
      if (this.state.selections[stepKey] && !this.autoResolved.has(stepKey)) {
        preserved[stepKey] = this.state.selections[stepKey];
      }
    });
    this.steps.slice(stepIndex).forEach((stepKey) => {
      delete this.state.selections[stepKey];
      this.autoResolved.delete(stepKey);
    });
    this.state.selections[key] = value;
    this.autoResolveSingles();

    for (const stepKey of downstream) {
      if (this.state.selections[stepKey]) continue; // already set by auto-resolve
      const wanted = preserved[stepKey];
      if (!wanted) break; // a gap downstream can't be bridged — stop here
      const stillValid = this.getOptionsForKey(stepKey).some((opt) => opt.value === wanted);
      if (!stillValid) break;
      this.autoResolved.delete(stepKey); // a re-applied value is a user choice, not auto
      this.state.selections[stepKey] = wanted;
    }
    this.autoResolveSingles(); // a re-applied choice may unlock a lone option below it
    this.state.resolvedConfig = this.getResolvedConfig();

    this.renderAllSteps();
    this.updateNav();
    this.updateSummary();
    this.updateVisual();

    if (this.state.resolvedConfig) {
      this.revealReview();
      this.scrollToEl(this.review.section);
    } else {
      this.hideReview();
      const nextKey = this.steps[this.firstIncompleteIndex()];
      if (nextKey) this.scrollToEl(this.container.querySelector(`.pb-step[data-step="${nextKey}"]`));
    }
  }

  handleNavClick(nav) {
    const state = nav.dataset.state;
    if (state === "locked") return;
    const key = nav.dataset.stepnav;
    const target =
      key === "review" ? this.review.section : this.container.querySelector(`.pb-step[data-step="${key}"]`);
    if (target && !target.hasAttribute("hidden")) this.scrollToEl(target);
  }

  firstIncompleteIndex() {
    for (let i = 0; i < this.steps.length; i += 1) {
      if (!this.state.selections[this.steps[i]]) return i;
    }
    return this.steps.length;
  }

  scrollToEl(el) {
    if (!el) return;
    el.scrollIntoView({ block: "start", behavior: this.reducedMotion ? "auto" : "smooth" });
  }

  /* ---------------------------------------------------------------
     Step rendering
  --------------------------------------------------------------- */

  renderAllSteps() {
    const reachable = this.firstIncompleteIndex();
    this.state.currentStep = Math.min(reachable + 1, this.steps.length);

    this.optionGroups.forEach((group) => {
      const key = group.dataset.optionGroup;
      const stepIndex = this.steps.indexOf(key);
      const stepEl = group.closest(".pb-step");
      const locked = stepIndex > reachable;

      if (stepEl) {
        stepEl.classList.toggle("is-locked", locked);
        stepEl.setAttribute("aria-disabled", locked ? "true" : "false");
      }

      if (locked) {
        const note = document.createElement("p");
        note.className = "pb-step__locked-note";
        note.textContent = this.translations.lockedNote || "Complete the previous step first.";
        group.replaceChildren(note);
        return;
      }

      this.renderOptions(group, key);
    });
  }

  renderOptions(group, key) {
    const options = this.getOptionsForKey(key);
    group.replaceChildren();

    if (!options.length) {
      const empty = document.createElement("p");
      empty.className = "pb-step__empty";
      empty.textContent = this.translations.noOptions || "No available options for the current selection.";
      group.appendChild(empty);
      return;
    }

    // board_model renders as a single family card with an in-card size picker.
    if (key === "board_model") {
      group.appendChild(this.buildModelCard(key, options));
      return;
    }

    const isSail = key === "sail_size";
    options.forEach((option) => {
      const node = isSail ? this.buildSizePill(key, option) : this.buildOptionCard(key, option);
      group.appendChild(node);
    });
  }

  buildOptionCard(key, option) {
    const selected = this.state.selections[key] === option.value;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "pb-option-card";
    button.dataset.packBuilderOption = "true";
    button.dataset.key = key;
    button.dataset.value = option.value;
    button.setAttribute("aria-pressed", selected ? "true" : "false");
    // Out-of-stock options stay fully selectable (no aria-disabled): stock is shown
    // as information, never a navigation block. Final availability resolves at add-to-cart.

    // Rig cards adopt the homepage .product-card visual language (gradient image
    // stage, dark card, mono price) to match the board card — same as home-products-grid.
    if (key === "rig_model") button.classList.add("pb-option-card--product");

    // Board and rig cards lead with a product image, set on a gradient stage for rigs.
    if ((key === "board_model" || key === "rig_model") && option.image) {
      const isProduct = key === "rig_model";
      const host = isProduct ? document.createElement("div") : button;
      if (isProduct) host.className = "pb-option-card__media";
      const thumb = document.createElement("img");
      thumb.className = "pb-option-card__thumb";
      thumb.src = option.image;
      thumb.alt = option.label;
      thumb.loading = "lazy";
      thumb.decoding = "async";
      host.appendChild(thumb);
      if (isProduct) button.appendChild(host);
    }

    const title = document.createElement("span");
    title.className = "pb-option-card__title";
    title.textContent = option.label;
    button.appendChild(title);

    if (option.description) {
      const sub = document.createElement("span");
      sub.className = "pb-option-card__sub";
      sub.textContent = option.description;
      button.appendChild(sub);
    }

    if (option.chips && option.chips.length) {
      const chips = document.createElement("span");
      chips.className = "pb-option-card__chips";
      option.chips.forEach((text) => {
        const chip = document.createElement("span");
        chip.className = "pb-chip";
        chip.textContent = text;
        chips.appendChild(chip);
      });
      button.appendChild(chips);
    }

    // Always show price (OOS items are selectable, so they keep their price);
    // append the out-of-stock badge as a secondary, non-blocking indicator.
    if (Number.isFinite(option.fromPrice)) {
      const price = document.createElement("span");
      price.className = "pb-option-card__price";
      price.innerHTML = `<span>${this.escape(this.translations.from || "from")}</span> ${this.escape(
        this.formatPrice(option.fromPrice),
      )}`;
      button.appendChild(price);
    }

    if (!option.available) {
      const oos = document.createElement("span");
      oos.className = "pb-option-card__oos";
      oos.textContent = this.translations.outOfStock || "Out of stock";
      button.appendChild(oos);
    }

    const check = document.createElement("span");
    check.className = "pb-option-card__check";
    button.appendChild(check);

    return button;
  }

  buildSizePill(key, option) {
    const selected = this.state.selections[key] === option.value;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "pb-size-pill";
    button.dataset.packBuilderOption = "true";
    button.dataset.key = key;
    button.dataset.value = option.value;
    button.setAttribute("aria-pressed", selected ? "true" : "false");
    button.setAttribute("aria-label", `${option.value} m² ${option.available ? "" : "(out of stock)"}`.trim());
    // Out-of-stock sizes stay selectable; the OOS badge below is informational only.

    const num = document.createElement("span");
    num.className = "pb-size-pill__num";
    num.textContent = option.value;
    button.appendChild(num);

    const unit = document.createElement("span");
    unit.className = "pb-size-pill__unit";
    unit.textContent = "m²";
    button.appendChild(unit);

    if (!option.available) {
      const oos = document.createElement("span");
      oos.className = "pb-size-pill__oos";
      oos.textContent = this.translations.outOfStock || "Out of stock";
      button.appendChild(oos);
    }

    return button;
  }

  // The board_model step renders as ONE card per family: product image on the
  // left, an in-card size/variant picker on the right. Each picker button is a
  // real board_model option (data-pack-builder-option), so the existing click
  // wiring selects it and advances. The active pick drives the image, chips and
  // price. Stock is shown, never blocks selection.
  buildModelCard(key, options) {
    const card = document.createElement("div");
    card.className = "pb-model-card";

    const selectedValue = this.state.selections[key];
    const active = options.find((opt) => opt.value === selectedValue) || options[0];
    const prices = options.map((opt) => opt.fromPrice).filter((n) => Number.isFinite(n));
    const minPrice = prices.length ? Math.min(...prices) : null;

    // Representative config for the active option drives the homepage-style meta
    // (construction badge, category, family name) — same visual language as the
    // home-products-grid .product-card.
    const activeConfig =
      active &&
      this.representativeConfig({
        board_type: this.state.selections.board_type,
        board_family: this.state.selections.board_family,
        board_model: active.value,
      });

    // Media (left)
    const media = document.createElement("div");
    media.className = "pb-model-card__media";

    if (activeConfig && activeConfig.board_construction_label) {
      const badge = document.createElement("span");
      const code = (activeConfig.board_construction || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      badge.className = `pb-card-badge pb-card-badge--${code}`;
      badge.textContent = activeConfig.board_construction_label.toUpperCase();
      media.appendChild(badge);
    }

    if (active && active.image) {
      const img = document.createElement("img");
      img.className = "pb-model-card__img";
      img.src = active.image;
      img.alt = active.label;
      img.loading = "lazy";
      img.decoding = "async";
      media.appendChild(img);
    }
    card.appendChild(media);

    // Body (right)
    const body = document.createElement("div");
    body.className = "pb-model-card__body";

    const familyLabel =
      this.labelFor("board_family", this.state.selections.board_family) || (active ? active.label : "");

    const category = activeConfig && (activeConfig.board_family_desc || activeConfig.board_type_label);
    if (category) {
      const cat = document.createElement("span");
      cat.className = "pb-model-card__category";
      cat.textContent = category;
      body.appendChild(cat);
    }

    const title = document.createElement("span");
    title.className = "pb-model-card__title";
    title.textContent = familyLabel;
    body.appendChild(title);

    const picker = document.createElement("div");
    picker.className = "pb-model-card__picker";
    options.forEach((option) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "pb-model-pick";
      btn.dataset.packBuilderOption = "true";
      btn.dataset.key = key;
      btn.dataset.value = option.value;
      const isActive = option.value === selectedValue;
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");

      const lbl = document.createElement("span");
      lbl.className = "pb-model-pick__label";
      lbl.textContent = option.label;
      btn.appendChild(lbl);

      if (!option.available) {
        const tag = document.createElement("span");
        tag.className = "pb-model-pick__oos";
        tag.textContent = this.translations.outOfStock || "Out of stock";
        btn.appendChild(tag);
      }
      picker.appendChild(btn);
    });
    body.appendChild(picker);

    const priceRow = document.createElement("div");
    priceRow.className = "pb-model-card__price-row";
    if (selectedValue && active && Number.isFinite(active.fromPrice)) {
      const price = document.createElement("span");
      price.className = "pb-model-card__price";
      price.textContent = this.formatPrice(active.fromPrice);
      priceRow.appendChild(price);
    } else if (Number.isFinite(minPrice)) {
      const price = document.createElement("span");
      price.className = "pb-model-card__price";
      price.innerHTML = `<span>${this.escape(this.translations.from || "from")}</span> ${this.escape(
        this.formatPrice(minPrice),
      )}`;
      priceRow.appendChild(price);
    }
    if (active && !active.available) {
      const oos = document.createElement("span");
      oos.className = "pb-model-card__oos";
      oos.textContent = this.translations.outOfStock || "Out of stock";
      priceRow.appendChild(oos);
    }
    body.appendChild(priceRow);

    card.appendChild(body);
    return card;
  }

  /* ---------------------------------------------------------------
     Option data (availability — unchanged logic)
  --------------------------------------------------------------- */

  getOptionsForKey(key) {
    const filtered = this.getConfigsForStep(key);
    const byValue = new Map();

    filtered.forEach((config) => {
      const rawValue = config[key];
      if (rawValue === undefined || rawValue === null || rawValue === "") return;
      const value = String(rawValue);
      const available = this.isOptionAvailable(key, value);
      const price = Number(config.price);
      const image = this.getOptionImage(key, config);
      const existing = byValue.get(value);

      if (existing) {
        existing.available = existing.available || available;
        if (Number.isFinite(price)) {
          existing.fromPrice = existing.fromPrice == null ? price : Math.min(existing.fromPrice, price);
        }
        if (!existing.image && image) existing.image = image;
        return;
      }

      byValue.set(value, {
        value,
        label: this.getOptionLabel(key, value, config),
        description: this.getOptionDescription(key, config),
        chips: this.getOptionChips(key, config),
        image,
        fromPrice: Number.isFinite(price) ? price : null,
        sort: config.sort_order || 0,
        available,
      });
    });

    // Board options list cheapest-first (price-driven packs feel more natural);
    // other steps keep their authored sort order.
    const byPrice = key === "board_family" || key === "board_model";
    return Array.from(byValue.values()).sort((a, b) => {
      if (byPrice) {
        const pa = Number.isFinite(a.fromPrice) ? a.fromPrice : Infinity;
        const pb = Number.isFinite(b.fromPrice) ? b.fromPrice : Infinity;
        if (pa !== pb) return pa - pb;
      }
      return a.sort - b.sort || this.naturalCompare(a.label, b.label);
    });
  }

  getConfigsForStep(key) {
    const stepIndex = this.steps.indexOf(key);
    const previousSteps = this.steps.slice(0, stepIndex);

    return this.configs.filter((config) =>
      previousSteps.every((stepKey) => {
        const selected = this.state.selections[stepKey];
        return !selected || String(config[stepKey]) === selected;
      }),
    );
  }

  isOptionAvailable(key, value) {
    const stepIndex = this.steps.indexOf(key);
    const testSelections = { ...this.state.selections, [key]: value };

    const matching = this.configs.filter((config) =>
      this.steps.slice(0, stepIndex + 1).every((stepKey) => {
        const selected = testSelections[stepKey];
        return !selected || String(config[stepKey]) === selected;
      }),
    );

    if (key === "sail_size") {
      const exact = matching.find((config) =>
        this.steps.every((stepKey) => String(config[stepKey]) === String(testSelections[stepKey])),
      );
      return Boolean(exact?.available);
    }

    return matching.some((config) => config.available);
  }

  getResolvedConfig() {
    if (!this.steps.every((key) => this.state.selections[key])) return null;

    return this.configs.find((config) =>
      this.steps.every((key) => String(config[key]) === String(this.state.selections[key])),
    );
  }

  // Auto-select any auto-skippable step that currently has exactly one in-stock
  // option, so the shopper never sees a 1-card screen. Idempotent: only ADDS new
  // auto-selections; marks are dropped when a step is cleared in the selection
  // cascade, so it stays consistent across repeated calls. Changing an upstream
  // step clears (and un-marks) a previously auto-resolved step, which then gets
  // re-evaluated here against the new context. Stock is not considered: a lone
  // option is auto-picked even when out of stock, since OOS never blocks selection.
  autoResolveSingles() {
    this.steps.forEach((key) => {
      if (!this.autoSkippable.has(key)) return;
      if (this.state.selections[key]) return;
      const options = this.getOptionsForKey(key);
      if (options.length === 1) {
        this.state.selections[key] = options[0].value;
        this.autoResolved.add(key);
      }
    });
  }

  // Guard: every enabled config must occupy a unique step-key path. A collision
  // means getResolvedConfig() could pick the wrong SKU for the same selections.
  assertUniqueResolution() {
    const seen = new Map();
    let unique = true;
    this.configs.forEach((config) => {
      const path = this.steps.map((key) => String(config[key])).join(" | ");
      if (seen.has(path)) {
        unique = false;
        console.error(
          `Pack Builder: resolution collision on [${path}] between SKUs ${seen.get(path)} and ${config.sku}.`,
        );
      } else {
        seen.set(path, config.sku);
      }
    });
    return unique;
  }

  representativeConfig(partial) {
    return this.configs.find((config) =>
      Object.keys(partial).every((key) => String(config[key]) === String(partial[key])),
    );
  }

  // Labels/descriptions/images are data-driven: pulled from the referenced
  // taxonomy entities (resolved in Liquid), never hardcoded here.
  getOptionLabel(key, value, config) {
    if (!config) return value;
    if (key === "board_type") return config.board_type_label || value;
    if (key === "board_family") return config.board_family_label || value;
    if (key === "board_model") return config.board_model_label || value;
    if (key === "rig_model") return config.rig_model_label || value;
    return value; // sail_size
  }

  getOptionDescription(key, config) {
    if (!config) return "";
    if (key === "board_type") {
      return config.board_type === "composite"
        ? this.translations.compositeDesc || "Rigid board — built for performance and feel."
        : this.translations.inflatableDesc || "Inflatable — packs down small, travels anywhere.";
    }
    if (key === "board_family") return config.board_family_desc || "";
    return "";
  }

  getOptionImage(key, config) {
    if (key === "board_type") return ""; // binary type cards are text-only (title + one-line sub)
    if (key === "board_model") return config.board_model_image || config.featured_image || "";
    if (key === "board_family") return config.board_family_image || config.featured_image || "";
    if (key === "rig_model") return config.rig_model_image || "";
    return config.featured_image || "";
  }

  // Resolve a human label for a selected value via a representative config.
  labelFor(key, value) {
    if (!value) return "";
    const config = this.configs.find((item) => String(item[key]) === String(value));
    return config ? this.getOptionLabel(key, value, config) : value;
  }

  getOptionChips(key, config) {
    if (key !== "board_model") return null;
    return [
      config.board_volume_l && `${config.board_volume_l} L`,
      config.board_length_cm && `${config.board_length_cm} cm`,
      config.board_width_cm && `${config.board_width_cm} cm`,
    ].filter(Boolean);
  }

  /* ---------------------------------------------------------------
     Visual anchor
  --------------------------------------------------------------- */

  updateVisual() {
    const sel = this.state.selections;
    const resolved = this.state.resolvedConfig;
    const fallback = this.settings.heroImage || this.visual.media?.dataset.defaultImage || "";

    let url = fallback;
    let title = "";
    let chips = [];

    if (resolved) {
      url = resolved.featured_image || fallback;
      title = resolved.pack_title || `${resolved.board_family_label} ${resolved.board_model_label}`;
      chips = [
        resolved.board_volume_l && `${resolved.board_volume_l} L`,
        resolved.board_length_cm && `${resolved.board_length_cm} cm`,
        resolved.sail_size && `${resolved.sail_size} m²`,
      ].filter(Boolean);
    } else if (sel.board_model) {
      const config = this.representativeConfig({
        board_type: sel.board_type,
        board_family: sel.board_family,
        board_model: sel.board_model,
      });
      if (config) {
        url = config.board_model_image || config.featured_image || fallback;
        title = `${config.board_family_label} ${config.board_model_label}`;
        chips = [
          config.board_volume_l && `${config.board_volume_l} L`,
          config.board_length_cm && `${config.board_length_cm} cm`,
          config.board_width_cm && `${config.board_width_cm} cm`,
        ].filter(Boolean);
      }
    } else if (sel.board_family) {
      const config = this.representativeConfig({
        board_type: sel.board_type,
        board_family: sel.board_family,
      });
      if (config) {
        url = config.board_family_image || config.featured_image || fallback;
        title = config.board_family_label || "";
      }
    }

    this.setVisualImage(url, title);
    this.renderOverlay(title, chips);
  }

  setVisualImage(url, alt) {
    const { media, img } = this.visual;
    if (!media || !img) return;

    if (!url) {
      this.currentVisual = null;
      media.classList.remove("has-image");
      img.classList.remove("is-loaded");
      img.removeAttribute("src");
      img.alt = "";
      return;
    }

    if (this.currentVisual === url) {
      media.classList.add("has-image");
      img.classList.add("is-loaded");
      return;
    }

    this.currentVisual = url;
    img.classList.remove("is-loaded"); // fade current out

    const preload = new Image();
    preload.onload = () => {
      if (this.currentVisual !== url) return; // a newer selection won
      img.src = url;
      img.alt = alt || "";
      media.classList.add("has-image");
      requestAnimationFrame(() => img.classList.add("is-loaded"));
    };
    preload.onerror = () => {
      if (this.currentVisual !== url) return;
      this.currentVisual = null;
      media.classList.remove("has-image");
      img.classList.remove("is-loaded");
    };
    preload.src = url;
  }

  renderOverlay(title, chips) {
    const overlay = this.visual.overlay;
    if (!overlay) return;
    overlay.replaceChildren();
    if (title) {
      const heading = document.createElement("p");
      heading.className = "pb-visual__title";
      heading.textContent = title;
      overlay.appendChild(heading);
    }
    (chips || []).forEach((text) => {
      const chip = document.createElement("span");
      chip.className = "pb-visual__chip";
      chip.textContent = text;
      overlay.appendChild(chip);
    });
  }

  /* ---------------------------------------------------------------
     Step navigation indicator
  --------------------------------------------------------------- */

  updateNav() {
    const reachable = this.firstIncompleteIndex();
    this.navItems.forEach((item) => {
      const key = item.dataset.stepnav;
      let state;
      if (key === "review") {
        state = this.state.resolvedConfig ? "current" : "locked";
      } else {
        const index = this.steps.indexOf(key);
        if (this.autoResolved.has(key)) state = "auto";
        else if (this.state.selections[key]) state = "done";
        else if (index === reachable) state = "current";
        else state = "locked";
      }
      item.dataset.state = state;
    });
  }

  /* ---------------------------------------------------------------
     Summary surfaces (desktop card + mobile bar)
  --------------------------------------------------------------- */

  updateSummary() {
    const sel = this.state.selections;
    const resolved = this.state.resolvedConfig;
    const available = Boolean(resolved && resolved.available);

    const boardText = [this.labelFor("board_family", sel.board_family), this.labelFor("board_model", sel.board_model)]
      .filter(Boolean)
      .join(" ");
    const rigText = this.labelFor("rig_model", sel.rig_model);
    const sailText = sel.sail_size ? `${sel.sail_size} m²` : "";
    const packText = resolved ? resolved.pack_title || "" : "";
    const priceDisplay = this.summaryPriceText(resolved);

    this.setSummary("board", boardText);
    this.setSummary("rig", rigText);
    this.setSummary("sail", sailText);
    this.setSummary("pack", packText);
    this.setSummary("price", priceDisplay);

    // Product thumbnails in the recap (board / rig / pack).
    const boardConfig = sel.board_model
      ? this.representativeConfig({
          board_type: sel.board_type,
          board_family: sel.board_family,
          board_model: sel.board_model,
        })
      : sel.board_family
        ? this.representativeConfig({ board_type: sel.board_type, board_family: sel.board_family })
        : null;
    const boardImg = boardConfig
      ? boardConfig.board_model_image || boardConfig.board_family_image || boardConfig.featured_image
      : "";
    const rigConfig = sel.rig_model ? this.representativeConfig({ rig_model: sel.rig_model }) : null;
    const rigImg = rigConfig ? rigConfig.rig_model_image || "" : "";
    const packImg = resolved ? resolved.featured_image || "" : "";

    this.setSummaryImage("board", boardImg, boardText);
    this.setSummaryImage("rig", rigImg, rigText);
    this.setSummaryImage("pack", packImg, packText);

    // Availability badge
    this.summaryNodes.forEach((node) => {
      if (node.dataset.summary !== "availability") return;
      if (!resolved) {
        node.hidden = true;
        return;
      }
      node.hidden = false;
      node.classList.toggle("pb-availability--in", available);
      node.classList.toggle("pb-availability--out", !available);
      node.textContent = available
        ? this.translations.available || "Available"
        : this.translations.unavailable || "Out of stock";
    });

    // Hint
    let hint = this.translations.completeHint || "Complete all steps to add your pack.";
    let warning = false;
    if (resolved && available) hint = "";
    else if (resolved && !available) {
      hint = this.translations.oosReviewMessage || "This pack is currently out of stock. Choose another sail size.";
      warning = true;
    }
    this.summaryNodes.forEach((node) => {
      if (node.dataset.summary !== "hint") return;
      node.textContent = hint;
      node.hidden = !hint;
      node.classList.toggle("is-warning", warning);
    });

    // Mobile bar text
    const selectedCount = this.steps.filter((key) => sel[key]).length;
    const mobileSummary = resolved
      ? packText
      : boardText || `${selectedCount}/${this.steps.length} ${this.translations.selectionCount || "selections made"}`;
    this.setSummary("mobile-summary", mobileSummary);
    this.setSummary("mobile-price", priceDisplay);

    // CTAs
    this.ctas.forEach((cta) => {
      cta.disabled = !available;
      cta.setAttribute("aria-disabled", available ? "false" : "true");
    });

    // Back-in-stock: when a pack resolves but is out of stock, offer the notify
    // form bound to the resolved variant (back-in-stock.js reads data-bis-variant-id
    // at submit). Hidden otherwise.
    this.updateBackInStock(resolved, available);

    // Mobile bar visibility
    if (this.mobilebar) {
      const show = Boolean(sel.board_type);
      this.mobilebar.classList.toggle("is-visible", show);
    }
  }

  setSummary(name, value) {
    this.summaryNodes.forEach((node) => {
      if (node.dataset.summary === name) node.textContent = value || "";
    });
  }

  updateBackInStock(resolved, available) {
    if (!this.bisBlocks || !this.bisBlocks.length) return;
    const show = Boolean(resolved && !available && resolved.variant_id);
    const variantId = show ? String(resolved.variant_id) : "";
    this.bisBlocks.forEach((block) => {
      const form = block.querySelector("[data-bis-form]");
      if (!form) return;
      if (show) {
        // Switching to a new OOS variant clears any prior success/error state.
        if (form.dataset.bisVariantId !== variantId) {
          form.dataset.bisVariantId = variantId;
          if (window.bisResetForm) window.bisResetForm(form);
          const email = form.querySelector("[data-bis-email]");
          const status = form.querySelector("[data-bis-status]");
          if (email) email.value = "";
          if (status) {
            status.textContent = "";
            status.className = "bis-form-2026__status";
          }
        }
        block.hidden = false;
      } else {
        if (form.dataset.bisVariantId && window.bisResetForm) window.bisResetForm(form);
        form.dataset.bisVariantId = "";
        block.hidden = true;
      }
    });
  }

  setSummaryImage(name, url, alt) {
    this.summaryImages.forEach((node) => {
      if (node.dataset.summaryImg !== name) return;
      if (url) {
        node.src = url;
        node.alt = alt || "";
        node.hidden = false;
      } else {
        node.removeAttribute("src");
        node.alt = "";
        node.hidden = true;
      }
    });
  }

  // Resolved -> exact pack price. Otherwise, once a board model is chosen, a
  // "from" teaser using the cheapest in-stock pack still reachable. Never on the
  // binary type step, where a catalog-wide minimum would mislead.
  summaryPriceText(resolved) {
    if (resolved) return this.formatPrice(resolved.price);
    if (!this.state.selections.board_model) return "—";
    const min = this.partialFromPrice();
    if (min == null) return "—";
    return `${this.translations.from || "from"} ${this.formatPrice(min)}`;
  }

  configsMatchingSelection() {
    const sel = this.state.selections;
    const setKeys = this.steps.filter((key) => sel[key]);
    return this.configs.filter((config) => setKeys.every((key) => String(config[key]) === String(sel[key])));
  }

  partialFromPrice() {
    const prices = this.configsMatchingSelection()
      .filter((config) => config.available)
      .map((config) => Number(config.price))
      .filter(Number.isFinite);
    return prices.length ? Math.min(...prices) : null;
  }

  /* ---------------------------------------------------------------
     Inline review
  --------------------------------------------------------------- */

  revealReview() {
    const config = this.state.resolvedConfig;
    if (!config || !this.review.section) return;
    this.review.section.hidden = false;
    this.updateReview(config);
    this.updateNav();
    // Move focus to the result heading so assistive tech lands on the resolved
    // pack (the aria-live region is unreliable when toggled from hidden).
    if (this.review.title) this.review.title.focus({ preventScroll: true });
    // Stock is rendered at page load; re-validate the resolved pack live before
    // the customer commits (fail-safe; the /cart/add.js guard is the backstop).
    this.refreshResolvedStock(config);
  }

  async refreshResolvedStock(config) {
    if (!config || !config.product_url || !config.variant_id) return;
    this.stockCache = this.stockCache || new Map();
    const key = String(config.variant_id);
    try {
      let live = this.stockCache.get(key);
      if (live === undefined) {
        const response = await fetch(`${config.product_url}.js`, { headers: { Accept: "application/json" }, signal: this.events.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (this.destroyed) return;
        const variant = (data.variants || []).find((item) => String(item.id) === key);
        live = variant ? Boolean(variant.available) : config.available;
        this.stockCache.set(key, live);
      }
      if (live === config.available) return;
      config.available = live;
      // Only re-render if this is still the resolved pack on screen.
      if (this.state.resolvedConfig === config) {
        this.updateReview(config);
        this.updateSummary();
        this.updateNav();
        this.renderAllSteps();
      }
    } catch (error) {
      // Network/parse failure -> keep render-time availability (non-blocking).
    }
  }

  hideReview() {
    if (this.review.section) this.review.section.hidden = true;
  }

  updateReview(config) {
    const available = Boolean(config.available);

    if (this.review.badge) {
      this.review.badge.textContent = available
        ? this.translations.reviewReady || "Pack resolved"
        : this.translations.reviewOut || "Out of stock";
      this.review.badge.classList.toggle("pb-review__badge--out", !available);
    }

    if (this.review.subtitle) {
      this.review.subtitle.textContent = available
        ? this.reviewSubtitleDefault
        : this.translations.oosReviewMessage || "This pack is currently out of stock. Choose another sail size.";
    }

    if (this.review.items) {
      // Prefer the authoritative pack contents (board, sail, mast, boom, ...) from
      // the product metafield; fall back to the resolved selections.
      const contents = Array.isArray(config.pack_contents)
        ? config.pack_contents.filter((item) => item && item.label)
        : null;
      if (contents && contents.length) {
        this.review.items.replaceChildren(
          ...contents.map((item) =>
            this.createReviewItem(item.label, item.spec ? `${item.value} (${item.spec})` : item.value),
          ),
        );
      } else {
        this.review.items.replaceChildren(
          this.createReviewItem("Board", `${config.board_family_label} ${config.board_model_label}`),
          this.createReviewItem("Rig", config.rig_model_label),
          this.createReviewItem("Sail", `${config.sail_size} m²`),
          this.createReviewItem("Pack", config.pack_title),
        );
      }
    }

    if (this.review.viewDetails) {
      if (config.product_url) {
        this.review.viewDetails.href = config.product_url;
        this.review.viewDetails.hidden = false;
      } else {
        this.review.viewDetails.hidden = true;
      }
    }

    if (this.review.chooseSize) this.review.chooseSize.hidden = available;
  }

  createReviewItem(label, value) {
    const item = document.createElement("span");
    item.className = "pb-review__item";
    const strong = document.createElement("strong");
    strong.textContent = `${label}: `;
    item.appendChild(strong);
    item.appendChild(document.createTextNode(value || "—"));
    return item;
  }

  markConfigUnavailable(config) {
    // Invalidate cached availability so refreshResolvedStock does not flip it back to true
    if (this.stockCache) this.stockCache.set(String(config.variant_id), false);
    config.available = false;
    this.state.resolvedConfig = config;
    this.updateReview(config);
    this.updateSummary();
    this.renderAllSteps();
    this.updateNav();
  }

  backToSailSizeStep() {
    delete this.state.selections.sail_size;
    this.state.resolvedConfig = null;
    this.hideReview();
    this.renderAllSteps();
    this.updateNav();
    this.updateSummary();
    this.updateVisual();
    this.scrollToEl(this.container.querySelector('.pb-step[data-step="sail_size"]'));
  }

  /* ---------------------------------------------------------------
     Empty state
  --------------------------------------------------------------- */

  showEmptyState() {
    if (this.configurator) {
      this.configurator.classList.add("is-empty");
      const empty = document.createElement("div");
      empty.className = "pb-empty";
      const heading = document.createElement("h2");
      heading.textContent = this.translations.emptyStateTitle || "Pack configurations are not available yet.";
      const body = document.createElement("p");
      body.textContent =
        this.translations.emptyStateBody || "Please check back soon or contact us for help choosing a pack.";
      empty.append(heading, body);
      this.configurator.replaceChildren(empty);
    }
    if (this.mobilebar) this.mobilebar.classList.remove("is-visible");
  }

  /* ---------------------------------------------------------------
     Cart
  --------------------------------------------------------------- */

  async handleAddPackToCart(button) {
    const config = this.state.resolvedConfig;
    if (!config || !config.variant_id || !config.available) return;

    const labelEl = button.querySelector(".pb-cta__label") || button;
    const originalText = labelEl.textContent;
    this.ctas.forEach((cta) => {
      cta.disabled = true;
    });
    labelEl.textContent = this.translations.adding || "Adding...";

    try {
      const data = await this.addResolvedPackToCart(config);
      if (this.destroyed) return;
      labelEl.textContent = this.translations.added || "Added";
      document.dispatchEvent(new CustomEvent("cart:add", { detail: data }));
      document.dispatchEvent(new CustomEvent("cart:open"));
      this.delay(() => {
        labelEl.textContent = originalText;
        this.updateSummary();
      }, 2000);
    } catch (error) {
      if (this.destroyed) return;
      console.error("Failed to add pack to cart", error);
      const soldOut = /sold out|out of stock|available/i.test(error.message || "");
      if (soldOut) {
        this.markConfigUnavailable(config);
        labelEl.textContent = this.translations.soldOutDuringAdd || "This pack just sold out. Choose another size.";
      } else {
        labelEl.textContent = this.translations.addError || "Error - Try Again";
        this.delay(() => {
          labelEl.textContent = originalText;
          this.updateSummary();
        }, 2500);
      }
    }
  }

  async addResolvedPackToCart(config) {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: config.variant_id,
            quantity: 1,
            properties: {
              _pack_builder: "true",
              _board_type: config.board_type_label || config.board_type,
              _board_construction: config.board_construction_label || config.board_construction,
              _board_family: config.board_family_label || config.board_family,
              _board_model: config.board_model_label || config.board_model,
              _rig_model: config.rig_model_label || config.rig_model,
              _sail_size: config.sail_size,
              _pack_config_sku: config.sku,
            },
          },
        ],
      }),
    });

    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || "Failed to add pack to cart");
    }

    return data;
  }

  restart() {
    this.state = {
      currentStep: 1,
      selections: {},
      resolvedConfig: null,
    };
    this.autoResolved = new Set();
    this.autoResolveSingles();
    this.hideReview();
    this.renderAllSteps();
    this.updateNav();
    this.updateSummary();
    this.updateVisual();
    this.scrollToEl(this.container.querySelector('.pb-step[data-step="board_type"]'));
  }

  /* ---------------------------------------------------------------
     Utils
  --------------------------------------------------------------- */

  formatPrice(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value)) return "";
    const currency = this.settings.currency || "EUR";
    const locale = (typeof Shopify !== "undefined" && Shopify.locale) || "en";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  naturalCompare(a, b) {
    const locale = (typeof Shopify !== "undefined" && Shopify.locale) || "en";
    return String(a).localeCompare(String(b), locale, { numeric: true, sensitivity: "base" });
  }

  escape(value) {
    return String(value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        default:
          return "&#39;";
      }
    });
  }
}

function initPackBuilder() {
  const container = document.querySelector('[id^="PackBuilder"]');
  if (window.packBuilderInstance?.container === container) return;
  window.packBuilderInstance?.destroy();
  window.packBuilderInstance = container ? new PackBuilder(container) : null;
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initPackBuilder);
else initPackBuilder();
document.addEventListener("shopify:section:load", initPackBuilder);
document.addEventListener("shopify:section:unload", (event) => {
  const instance = window.packBuilderInstance;
  if (instance?.container && event.target.contains(instance.container)) {
    instance.destroy();
    window.packBuilderInstance = null;
  }
});

if (typeof module !== "undefined" && module.exports) module.exports = PackBuilder;
